import React, { useEffect, useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, Button } from '@mui/material';
import { showNotificationBar, NOTIFICATION_TYPE } from '../../../actions/error-pages/NotificationBarActions';
import { ContainedButton, LoadingButton, TextButton } from '../../common/StyledButtons';
import { withRouter } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { StyledTextField } from '../../common/StyledTextField';
import { connectSiteLightsailConnection, updateSiteInformation } from '../../../actions/internal/SiteActions';
import { getUserId } from '../../../selectors/UserSelectors';
import Colors from '../../../constants/Colors';

const AdminConfigureLightsailProtectionModal = ({ open, onClose, fields, site }) => {

  const [values, setValues] = useState({
    accountId: '',
    instanceName: '',
    instanceRegion: '',
    instanceIp: '',
    wasPreviouslyConfigured: false
  });
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [ connectionError, setConnectionError ] = useState();

  useEffect(() => {
    if (!fields) return;
    setValues({
      accountId: fields.aws_account_id,
      instanceName: fields.lightsail_instance_name,
      instanceRegion: fields.lightsail_region,
      instanceIp: fields.lightsail_ip,
      wasPreviouslyConfigured: true
    })
  }, [fields])

  const setFieldValue = (id, value) => {
    setValues({ ...values, [id]: value });
  };

  const clearForm = () => {
    setValues({ accountId: '', instanceName: '', instanceRegion: '', instanceIp: '' });
    setErrors([]);
    setConnectionError();
  };

  const handleClose = () => {
    clearForm();
    onClose();
  };

  const validateFields = () => {
    const missingFields = [];
    if (!values.accountId.trim()) missingFields.push('accountId');
    if (!values.instanceName.trim()) missingFields.push('instanceName');
    if (!values.instanceRegion.trim()) missingFields.push('instanceRegion');
    if (values.instanceIp && values.instanceIp.trim() !== '') {
      const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
      if (!ipRegex.test(values.instanceIp.trim())) missingFields.push('instanceIp');
    }
    return missingFields;
  };

  const prettyPrintObject = (obj) => {
    if (typeof obj !== 'object' || obj === null) {
        return String(obj);
    }

    const pretty = JSON.stringify(obj, null, 2)
        .replace(/</g, '&lt;')  // Escape < and >
        .replace(/>/g, '&gt;')
        .replace(/ /g, '&nbsp;')
        .replace(/\n/g, '<br>');

    return pretty;
}

  const handleSave = async () => {
    setConnectionError();

    const validationErrors = validateFields();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      showNotificationBar(NOTIFICATION_TYPE.ERROR, 'Please fill out required fields and check IP format.');
      return;
    }
    setIsLoading(true);
    try {
      const lightsail = { 
        aws_account_id: values.accountId,
        lightsail_instance_name: values.instanceName,
        lightsail_region: values.instanceRegion,
        lightsail_ip: values.instanceIp && values.instanceIp.trim() !== '' ? values.instanceIp : undefined
      }

      await connectSiteLightsailConnection(getUserId(), site.site_id, lightsail);

      showNotificationBar(NOTIFICATION_TYPE.SUCCESS, 'Lightsail Protection applied.');
      handleClose();
    } catch (ex) {
      const awaitedError = await ex;
      setConnectionError(awaitedError.message)
      console.error(awaitedError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog fullWidth open={open} maxWidth={'sm'}>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <h4 style={{ margin: 0, padding: 0 }}>Add Request</h4>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <StyledTextField
            label="AWS Account ID*"
            value={values.accountId}
            onChange={(e) => setFieldValue('accountId', e.target.value)}
            error={errors.includes('accountId')}
          />
          <StyledTextField
            label="Instance Name*"
            value={values.instanceName}
            onChange={(e) => setFieldValue('instanceName', e.target.value)}
            error={errors.includes('instanceName')}
          />
          <StyledTextField
            label="Instance Region*"
            value={values.instanceRegion}
            onChange={(e) => setFieldValue('instanceRegion', e.target.value)}
            error={errors.includes('instanceRegion')}
          />
          <StyledTextField
            label="Instance IP Address"
            value={values.instanceIp}
            onChange={(e) => setFieldValue('instanceIp', e.target.value)}
            error={errors.includes('instanceIp')}
            helperText="Optional. If provided, must be a valid IPv4 address."
          />
        </Box>
        {(connectionError) &&
        <Box sx={{ border: '1px solid #ccc', borderRadius: 2, p: 1 }}>
          <h4 style={{ fontSize: '20px', textAlign: 'center', paddingTop: '10px' }}>Connection Unsuccessful</h4>
          {connectionError && <p style={{ color: Colors.redColor }} dangerouslySetInnerHTML={{ __html: prettyPrintObject(connectionError) }}></p>}
        </Box>
        }
        
      </DialogContent>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
        <Box>
          <TextButton sx={{ marginRight: '10px' }} onClick={handleClose} disabled={isLoading}>
            Cancel
          </TextButton>
          <LoadingButton onClick={handleSave} isLoading={isLoading}>
            Connect
          </LoadingButton>
        </Box>
      </Box>
    </Dialog>
  );
};

export default observer(withRouter(AdminConfigureLightsailProtectionModal));
